























































































































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import draggable from 'vuedraggable';
import { Props, User, Schedules, Global, Groups } from '@/store';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import PropEditor from '@/components/PropEditor.vue';
import MessageDialog from '@/components/dialogs/MessageDialog.vue';
import cloneDeep from 'lodash/cloneDeep';
import { Types } from '@/modules/API';

@Component({
  components: {ConfirmDialog, PropEditor, draggable, MessageDialog}
})
class ScheduleManagement extends Vue {
  @User.State('project') project;
  @Props.State('list') properties;
  @Schedules.Action('loadSchedules') loadSchedules;
  @Schedules.State('schedules') schedules;
  @Schedules.Mutation('schedules') setSchedules;
  @Schedules.Action('runSchedulerInServer') runSchedulerInServer;
  @Groups.State('list') groups;
  @Groups.Action('load') loadGroups;
  @Global.State('lang') lang;
  @Global.Mutation('setPageTitle') setPageTitle;
  @Global.State('timezone') projectTimezone;
  @Global.State('readonly_user') readonly_user;

  isMessageDialogOpen = false;
  message = 'There is no timezone for this project';
  info = 'Please contact with operation team';
  currentHeaderClass = '';
  isLoading = false;
  group = null;
  deleteDialog = false;
  schedule_to_delete = null;
  has_changes = false;
  saveDialog = false;
  schedules_copy = [];
  route_to = null;
  saveMessage = '';
  checkbox_color = '#812E81';
  search = '';
  group_filter = [];

  mounted() {
    this.setPageTitle('Schedule Management');
    this.load();
  }

  updated() {
    this.currentHeaderClass = this.lang === 'en' ? 'text-xs-left' : 'text-xs-right';
  }

  /** Group id -> group name, for resolving the ids stored on each schedule. */
  get groups_names_map(){
    const map = {};
    (this.groups || []).forEach((group) => map[group.id] = group.name);
    return map;
  }

  /** Schedule id -> sorted names of the groups it is applied to. */
  get schedule_groups_map(){
    const map = {};
    this.schedules_copy.forEach((schedule) => {
      map[schedule.id] = (Array.isArray(schedule.groups) ? schedule.groups : [])
        .map((group_id) => this.groups_names_map[group_id] || group_id)
        .sort((name1, name2) => `${name1}`.localeCompare(`${name2}`));
    });
    return map;
  }

  /** Only groups actually applied to a schedule are worth filtering by. */
  get filter_groups_list(){
    const assigned_ids = new Set<string>();
    this.schedules_copy.forEach((schedule) => {
      if (Array.isArray(schedule.groups)) schedule.groups.forEach((group_id) => assigned_ids.add(group_id));
    });

    return [...assigned_ids]
      .map((group_id) => ({ id: group_id, name: this.groups_names_map[group_id] || group_id }))
      .sort((group1, group2) => `${group1.name}`.localeCompare(`${group2.name}`));
  }

  get is_filtering(){
    return !!(this.search || '').trim() || this.group_filter.length > 0;
  }

  get filtered_schedules(){
    const query = (this.search || '').trim().toLowerCase();

    return this.schedules_copy.filter((schedule) => {
      const matches_search = !query || `${schedule.name || ''}`.toLowerCase().includes(query);
      const matches_groups = !this.group_filter.length ||
        (Array.isArray(schedule.groups) && schedule.groups.some((group_id) => this.group_filter.includes(group_id)));

      return matches_search && matches_groups;
    });
  }

  /** Reordering by drag only makes sense while the whole, unfiltered list is on screen. */
  get is_draggable(){
    return !this.readonly_user && !this.is_filtering;
  }

  clearFilters(){
    this.search = '';
    this.group_filter = [];
  }

  handleSaveClicked(){
    this.saveDialog = true;
    this.saveMessage = 'Are you sure you want to save changes?';
  }

  handleSaveCancled(){
    this.saveDialog = false;
    if (this.route_to) {
      this.has_changes = false;
      this.$router.push(this.route_to.fullPath);
    }
  }

  goToSchedule(){
    this.projectTimezone
      ? this.$router.push('/schedules/create')
      : this.isMessageDialogOpen = true;
  }

  handleCheckbox(item){
    this.has_changes = true;
    const schedule = this.schedules_copy.find((schedule) => schedule.id === item.id);
    schedule.active = item.active ? 0 : 1;
  }

  handleChange(){
    this.has_changes = true;
    this.schedules_copy.forEach((schedule, index) => schedule.priority = index + 1);
  }

  updateScheduleProperty(){
    const schedule_property = this.properties.find((item) => item.name === 'dashboard.schedules');
    if (schedule_property.value) {
      schedule_property.value = this.schedules_copy;
      this.setSchedules(schedule_property);
    }
  }

  @Watch('schedules') 
  handleSchedules(){
    this.schedules_copy = cloneDeep(this.schedules);
  }

  @Watch('project')
  async load() {
    if (this.project){
      this.isLoading = true;
      this.clearFilters();
      await Promise.all([
        this.loadSchedules({projectId: this.project.id}),
        this.loadGroups({project_id: this.project.id})
      ]);
      this.isLoading = false;
    }
  }

  removeSchedule() {
    this.has_changes = true;
    this.schedules_copy = this.schedules_copy.filter((schedule) => schedule.id !== this.schedule_to_delete);
    this.schedules_copy.forEach((schedule, index) => schedule.priority = index + 1);
    this.schedule_to_delete = null;
    this.deleteDialog = false;
  }

  openDeleteDialog(schedule_id) {
    this.deleteDialog = true;
    this.schedule_to_delete = schedule_id;
  }

  async saveChanges(){
    (this.$refs.propEditor as Vue & { save: (value: any, prop: any, callback: () => void) => void }).save(this.schedules_copy, {
      name: 'dashboard.schedules',
      objectID: this.project.id,
      objectType: Types.PROJECTS
    }, () => {
      this.saveDialog = false;
      this.$notify({
        text: 'Schedules Updated Successfully',
        type: 'success',
        title: 'Success'
      });
      this.runSchedulerInServer();
      this.has_changes = false;
      if (this.route_to) this.$router.push(this.route_to.fullPath);
    });
  }

  beforeRouteLeave(to, from, next) {
    this.route_to = to;

    if (this.has_changes) {
      this.saveMessage = 'Save changes before exiting?';
      this.saveDialog = true;
    }else next();
  }
}

export default ScheduleManagement;
